/**
 * useUserForm Hook
 * Centraliserad state management och logik för användarformulär
 */

import { useState, useEffect, useCallback } from 'react';
import { useCrudOperations } from '../../../components/Hooks/useCrudOperations';
import { API_URL, getAvatarUrl } from '../../../utils/api';
import { invalidateGlobalCache } from '../../../hooks/useApiCache';
import { validateUserForm, validateField } from '../domain/validation';
import { 
  prepareUserDataForCreate, 
  prepareUserDataForUpdate, 
  convertToFormDataIfNeeded,
  getCompanyNameForRole,
  validateAvatarFile
} from '../domain/userTransforms';
import { fetchSuppliers, sendWelcomeEmail as sendWelcomeEmailApi } from '../api/usersApi';

/**
 * Custom hook för användarformulär
 * @param {Object} options - Konfiguration
 * @param {Object} options.initialUser - Initial användardata (för uppdatering)
 * @param {Function} options.onClose - Callback när formuläret stängs
 * @param {Function} options.onSuccess - Callback vid lyckad operation
 * @param {Function} options.setUsers - State setter för användarlista
 * @param {Array} options.users - Användarlista
 * @param {string} options.defaultRole - Default roll (för nya användare)
 * @param {boolean} options.isUpdate - Om det är uppdateringsläge
 * @returns {Object} Form state och handlers
 */
export const useUserForm = ({
  initialUser = null,
  onClose,
  onSuccess,
  setUsers,
  users,
  defaultRole = '',
  isUpdate = false
}) => {
  // Initial state för nya användare
  const getInitialValues = useCallback(() => {
    if (isUpdate && initialUser) {
      return {
        firstName: initialUser.firstName || '',
        lastName: initialUser.lastName || '',
        email: initialUser.email || '',
        phone: initialUser.phone || '',
        role: initialUser.role || '',
        selectedBuilder: initialUser.builder?._id || initialUser.builder || '',
        selectedSupplier: initialUser.supplier?._id || initialUser.supplier || '',
        password: '',
        avatar: initialUser.avatar || '',
        removeAvatar: false,
        companyName: initialUser.companyName || '',
        contractorType: initialUser.contractorType || '',
        projectorType: initialUser.projectorType || '',
        includeInProjektorList: initialUser.includeInProjektorList !== false,
        requirePasswordChange: initialUser.requirePasswordChange === true,
        canViewAllProjects: initialUser.canViewAllProjects === true,
        hasAdminAccess: initialUser.hasAdminAccess === true,
        hasBookingAccess: initialUser.hasBookingAccess === true,
        hasFeedbackAccess: initialUser.hasFeedbackAccess === true,
        hasPricingAccess: initialUser.hasPricingAccess === true,
        hasMarketingAccess: initialUser.hasMarketingAccess === true,
        canEditProjektorer: initialUser.canEditProjektorer === true,
        isTestCustomer: initialUser.isTestCustomer === true,
        marketingTabAccess: {
          fotografering: initialUser.marketingTabAccess?.fotografering === true,
          contentPlan:   initialUser.marketingTabAccess?.contentPlan === true,
          ideer:         initialUser.marketingTabAccess?.ideer === true
        }
      };
    }

    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: defaultRole,
      selectedBuilder: '',
      password: '',
      avatar: '',
      companyName: '',
      selectedSupplier: '',
      contractorType: '',
      projectorType: '',
      includeInProjektorList: true,
      requirePasswordChange: false,
      canViewAllProjects: false,
      hasAdminAccess: false,
      hasBookingAccess: false,
      hasFeedbackAccess: false,
      hasPricingAccess: false,
      hasMarketingAccess: false,
      canEditProjektorer: false,
      isTestCustomer: false,
      marketingTabAccess: { fotografering: false, contentPlan: false, ideer: false },
      sendWelcomeEmail: true,
      removeAvatar: false
    };
  }, [initialUser, isUpdate, defaultRole]);

  const [values, setValues] = useState(getInitialValues());
  const [suppliers, setSuppliers] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(
    isUpdate && initialUser?.avatar ? getAvatarUrl(initialUser.avatar) : ''
  );
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const { addItem, updateItem } = useCrudOperations(`${API_URL}/users`, setUsers, users);

  // Hämta leverantörer
  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const data = await fetchSuppliers();
        setSuppliers(data);
      } catch (error) {
        console.error('Fel vid hämtning av leverantörer:', error);
      }
    };
    loadSuppliers();
  }, []);

  // Uppdatera state när initialUser ändras (för UpdateUser)
  useEffect(() => {
    if (isUpdate && initialUser) {
      setValues(getInitialValues());
      setPreviewUrl(initialUser.avatar ? getAvatarUrl(initialUser.avatar) : '');
    }
  }, [initialUser, isUpdate, getInitialValues]);

  /**
   * Hanterar ändringar i formulärfält
   */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    
    // Real-time validering för specifika fält
    if (name === 'phone' || name === 'email') {
      const validation = validateField(name, value);
      setValues(prev => ({ 
        ...prev, 
        [name]: validation.formattedValue 
      }));
      setErrors(prev => ({ 
        ...prev, 
        [name]: validation.errorMessage 
      }));
      return;
    }
    
    // Hantera roll-ändringar
    if (name === 'role') {
      const companyNameValue = getCompanyNameForRole(value, values.companyName);
      setValues(prev => ({
        ...prev,
        role: value,
        companyName: companyNameValue,
        selectedSupplier: '', // Återställ när roll ändras
        contractorType: value === 'UE' ? prev.contractorType : '',
        projectorType: value === 'projektor' ? prev.projectorType : ''
      }));
      return;
    }
    
    // Default ändringshantering
    setValues(prev => ({ ...prev, [name]: value }));
  }, [values.companyName]);

  /**
   * Hanterar avatar-uppladdning
   */
  const handleAvatarChange = useCallback((file) => {
    const validation = validateAvatarFile(file);
    
    if (!validation.isValid) {
      setErrors(prev => ({ ...prev, avatar: validation.error }));
      return;
    }
    
    setValues(prev => ({ ...prev, avatar: file, removeAvatar: false }));
    setPreviewUrl(URL.createObjectURL(file));
    setErrors(prev => ({ ...prev, avatar: '' }));
  }, []);

  /**
   * Hanterar borttagning av avatar
   */
  const handleRemoveAvatar = useCallback(() => {
    setPreviewUrl('');
    setValues(prev => ({ ...prev, avatar: '', removeAvatar: true }));
  }, []);

  /**
   * Invaliderar relevanta caches efter CRUD-operation
   */
  const invalidateCaches = useCallback(() => {
    invalidateGlobalCache([
      'dashboard_users',
      'myassigned_users',
      'dashboard_projects',
      'myassigned_projects',
      'myprojects_projects'
    ]);
  }, []);

  /**
   * Hanterar formulärsubmit
   */
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    setEmailSent(false);

    // Validera formulär
    const validationErrors = validateUserForm(values, isUpdate);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsLoading(false);
      return;
    }

    try {
      let userData;
      
      // Förbered data baserat på operation
      if (isUpdate) {
        userData = prepareUserDataForUpdate(values, initialUser);
      } else {
        userData = prepareUserDataForCreate(values);
      }
      
      // Konvertera till FormData om behövs
      const { body, isFormData } = convertToFormDataIfNeeded(userData);
      
      // Utför operation
      let resultUser;
      if (isUpdate) {
        await updateItem(initialUser._id, body, isFormData);
        resultUser = { ...initialUser, ...values };
      } else {
        resultUser = await addItem(body, isFormData);
        
        // Skicka välkomstmail för nya användare om valt
        if (values.sendWelcomeEmail) {
          try {
            await sendWelcomeEmailApi(resultUser, values.password);
            setEmailSent(true);
          } catch (emailError) {
            console.error('Kunde inte skicka välkomstmail:', emailError);
          }
        }
      }
      
      // Invalidera caches
      invalidateCaches();
      
      // Stäng formulär och kalla callbacks
      onClose();
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error) {
      console.error('Form submission error:', error);
      setErrorMessage(isUpdate ? 'Kunde inte uppdatera användaren' : 'Kunde inte skapa användaren');
      setErrors(prev => ({
        ...prev,
        submit: isUpdate ? 'Kunde inte uppdatera användaren' : 'Kunde inte skapa användaren'
      }));
    } finally {
      setIsLoading(false);
    }
  }, [values, isUpdate, initialUser, addItem, updateItem, invalidateCaches, onClose, onSuccess]);

  /**
   * Skickar välkomstmail (för UpdateUser)
   */
  const handleSendWelcomeEmail = useCallback(async () => {
    if (!values.password) {
      setErrorMessage('Du måste ange ett lösenord för att skicka välkomstmail');
      return;
    }

    setIsSendingEmail(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const userToSend = {
        ...initialUser,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email
      };
      
      await sendWelcomeEmailApi(userToSend, values.password);
      setSuccessMessage('Välkomstmail har skickats till användaren!');
    } catch (error) {
      console.error('Fel vid skickande av välkomstmail:', error);
      setErrorMessage('Ett fel uppstod vid skickande av välkomstmail');
    } finally {
      setIsSendingEmail(false);
    }
  }, [values, initialUser]);

  return {
    // State
    values,
    setValues,
    suppliers,
    previewUrl,
    errors,
    isLoading,
    isSendingEmail,
    emailSent,
    errorMessage,
    successMessage,
    
    // Handlers
    handleChange,
    handleAvatarChange,
    handleRemoveAvatar,
    handleSubmit,
    handleSendWelcomeEmail
  };
};

